export interface InputHandlers {
  next: () => void;
  previous: () => void;
  toggleNotes: () => void;
  toggleFullscreen: () => void;
  toggleHud: () => void;
  toggleHelp: () => void;
  dismiss: () => void;
  restart: () => void;
  jumpTo: (index: number) => void;
  first: () => void;
  last: () => void;
  wakeControls: () => void;
}

/**
 * Desktop input only: keyboard, mouse click and wheel. Touch gestures are deliberately not
 * supported (docs/design-system/README.md — desktop only). Nothing here ever advances on a timer.
 */
export class InputController {
  private wheelLocked = false;

  constructor(
    private readonly surface: HTMLElement,
    private readonly handlers: InputHandlers,
  ) {}

  public bind(): void {
    window.addEventListener('keydown', this.onKeyDown);
    this.surface.addEventListener('click', this.onClick);
    this.surface.addEventListener('wheel', this.onWheel, { passive: false });
    this.surface.addEventListener('pointermove', this.onPointerMove, { passive: true });
  }

  public destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    this.surface.removeEventListener('click', this.onClick);
    this.surface.removeEventListener('wheel', this.onWheel);
    this.surface.removeEventListener('pointermove', this.onPointerMove);
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target?.matches('input, textarea, select, [contenteditable="true"]')) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    this.handlers.wakeControls();

    // A focused control handles its own Enter/Space; everything else drives the presentation.
    const onControl = !!target?.closest('button, a');
    if (onControl && (event.key === 'Enter' || event.key === ' ')) return;

    if ([' ', 'ArrowRight', 'ArrowDown', 'PageDown', 'Enter'].includes(event.key)) {
      event.preventDefault();
      this.handlers.next();
      return;
    }

    if (['ArrowLeft', 'ArrowUp', 'PageUp', 'Backspace'].includes(event.key)) {
      event.preventDefault();
      this.handlers.previous();
      return;
    }

    if (event.key === 'Home') { event.preventDefault(); this.handlers.first(); return; }
    if (event.key === 'End') { event.preventDefault(); this.handlers.last(); return; }
    if (event.key === 'Escape') { this.handlers.dismiss(); return; }
    if (event.key === '?') { this.handlers.toggleHelp(); return; }

    const key = event.key.toLowerCase();
    if (key === 'n') this.handlers.toggleNotes();
    if (key === 'f') this.handlers.toggleFullscreen();
    if (key === 'h') this.handlers.toggleHud();
    if (key === 'r') this.handlers.restart();

    // Number keys match the HUD counter: 1 = first scene … 9 = ninth, 0 = tenth.
    if (/^[0-9]$/.test(event.key)) {
      const numeric = Number(event.key);
      this.handlers.jumpTo(numeric === 0 ? 9 : numeric - 1);
    }
  };

  private onClick = (event: MouseEvent): void => {
    this.handlers.wakeControls();
    const target = event.target as HTMLElement;
    if (target.closest('button, a, .progress-map, .presenter-notes, .keyboard-help')) return;
    this.handlers.next();
  };

  private onWheel = (event: WheelEvent): void => {
    this.handlers.wakeControls();
    if (Math.abs(event.deltaY) < 18 || this.wheelLocked) return;
    event.preventDefault();
    this.wheelLocked = true;
    if (event.deltaY > 0) this.handlers.next();
    else this.handlers.previous();
    window.setTimeout(() => { this.wheelLocked = false; }, 430);
  };

  private onPointerMove = (): void => {
    this.handlers.wakeControls();
  };
}
